import { useEffect, useState } from 'react';
import { calculateBarChartPlotAreaBottomInset } from './barChartPlotAreaBottomInset';
import type { RefObject } from 'react';

type UseBarChartPlotAreaBottomInsetParams = {
  chartMarginBottom: number;
  containerRef: RefObject<HTMLDivElement | null>;
  data: ReadonlyArray<Record<string, string | number | null>>;
  height: number;
  series: ReadonlyArray<{ color?: string; key: string; label: string }>;
};

const useBarChartPlotAreaBottomInset = ({
  chartMarginBottom,
  containerRef,
  data,
  height,
  series,
}: UseBarChartPlotAreaBottomInsetParams): number | null => {
  const [plotAreaBottomInset, setPlotAreaBottomInset] = useState<number | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    let frameId = 0;

    const updatePlotAreaBottomInset = (): void => {
      const xAxisLine = container.querySelector(
        '.MuiChartsAxis-directionX .MuiChartsAxis-line',
      ) as SVGGraphicsElement | null;
      if (!xAxisLine) {
        setPlotAreaBottomInset(chartMarginBottom);
        return;
      }
      const containerRect = container.getBoundingClientRect();
      const xAxisRect = xAxisLine.getBoundingClientRect();
      const nextBottomInset = calculateBarChartPlotAreaBottomInset({
        chartMarginBottom,
        containerBottom: containerRect.bottom,
        xAxisTop: xAxisRect.top,
      });
      setPlotAreaBottomInset((currentValue) =>
        currentValue !== null && Math.abs(currentValue - nextBottomInset) < 1
          ? currentValue
          : nextBottomInset,
      );
    };

    const scheduleUpdate = (): void => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      frameId = requestAnimationFrame(updatePlotAreaBottomInset);
    };

    scheduleUpdate();
    const resizeObserver = new ResizeObserver(() => {
      scheduleUpdate();
    });
    resizeObserver.observe(container);
    const svg = container.querySelector('svg');
    if (svg instanceof SVGSVGElement) {
      resizeObserver.observe(svg);
    }
    window.addEventListener('resize', scheduleUpdate);

    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      resizeObserver.disconnect();
      window.removeEventListener('resize', scheduleUpdate);
    };
  }, [chartMarginBottom, containerRef, data, height, series]);

  return plotAreaBottomInset;
};

export { useBarChartPlotAreaBottomInset };
